<template>
  <div class="verify-page">
    <div class="flex justify-content-center align-items-center min-h-screen p-4">
      <Card class="verify-card shadow-4 border-round-xl overflow-hidden backdrop-blur-sm">
        <template #title>
          <div class="verify-header p-5 -m-3 text-white relative overflow-hidden">
            <div class="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-blue-600/10"></div>
            <div class="flex align-items-center justify-content-center flex-column text-center relative z-10">
              <div class="logo-container mb-4">
                <img :src="Logo" alt="FeedWiz Logo" class="verify-logo">
              </div>
              <h1 class="text-2xl font-bold m-0 mb-1 text-white">Vérification Email</h1>
            </div>
          </div>
        </template>
        <template #content>
          <div class="p-fluid p-6">
            <!-- Loading -->
            <div v-if="loading" class="flex flex-column align-items-center gap-4 py-4">
              <i class="pi pi-spin pi-spinner text-blue-500" style="font-size: 4rem;"></i>
              <p class="text-gray-600 text-center m-0">Vérification en cours...</p>
            </div>

            <!-- Success -->
            <div v-else-if="success" class="flex flex-column align-items-center gap-4 py-4">
              <i class="pi pi-check-circle text-green-500" style="font-size: 4rem;"></i>
              <h2 class="text-xl font-bold text-gray-800 m-0 text-center">Email vérifié !</h2>
              <p class="text-gray-600 text-center m-0">
                Votre compte a été activé avec succès.<br>
                Vous allez être redirigé vers la connexion...
              </p>
              <router-link to="/login">
                <Button label="Se connecter" icon="pi pi-sign-in" class="p-button-primary" />
              </router-link>
            </div>

            <!-- Error -->
            <div v-else-if="error" class="flex flex-column align-items-center gap-4 py-4">
              <i class="pi pi-times-circle text-red-500" style="font-size: 4rem;"></i>
              <h2 class="text-xl font-bold text-gray-800 m-0 text-center">Erreur de vérification</h2>
              <p class="text-gray-600 text-center m-0">{{ error }}</p>
              <router-link to="/signup">
                <Button label="Réessayer l'inscription" icon="pi pi-refresh" class="p-button-secondary" />
              </router-link>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { applyActionCode } from 'firebase/auth'
import { auth } from '@/firebase'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Logo from '@/assets/feedwiz-logo-white-new-2.png'

const route = useRoute()
const router = useRouter()

const loading = ref(true)
const success = ref(false)
const error = ref(null)

onMounted(async () => {
  const mode = route.query.mode
  const oobCode = route.query.oobCode

  if (mode !== 'verifyEmail' || !oobCode) {
    error.value = 'Lien de vérification invalide.'
    loading.value = false
    return
  }

  try {
    await applyActionCode(auth, oobCode)
    success.value = true
    // Redirection auto après 3 secondes
    setTimeout(() => {
      router.push('/login')
    }, 3000)
  } catch (err) {
    console.error('Verification error:', err)
    if (err.code === 'auth/invalid-action-code') {
      error.value = 'Ce lien a expiré ou a déjà été utilisé.'
    } else if (err.code === 'auth/expired-action-code') {
      error.value = 'Ce lien a expiré. Veuillez vous réinscrire.'
    } else {
      error.value = 'Une erreur est survenue lors de la vérification.'
    }
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.verify-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding: 0.5rem;
}

.verify-card {
  width: 100%;
  max-width: 420px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.verify-logo {
  height: 4rem;
  width: auto;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
}

.verify-header {
  background: linear-gradient(135deg, #4a89dc 0%, #5ca0f2 50%, #67b7f7 100%);
  position: relative;
  padding: 1.5rem !important;
}

.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (min-width: 576px) {
  .verify-logo {
    height: 5rem;
  }
  .verify-header {
    padding: 2rem !important;
  }
}
</style>